import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Sparkles } from 'lucide-react'
import { buttonVariants } from '@/components/ui/basis/button'
import { RECORD_RESULT, waitTaskLine } from '@/components/orders/feasibilityText'
import type { TaskRead } from '@/types/process'
import { FieldBlock } from './FieldBlock'
import { TaskActions, type TaskAction } from './TaskActions'
import { SAVE_DRAFT, fillFirst } from './taskActionsText'
import { NeedsList } from './NeedsList'
import { ProposalRationale } from './ProposalRationale'
import { SUGGESTED_BY_AI } from './proposalText'
import { RESTRICTED_TASK } from './restrictedText'
import { tourAnchor } from '@/components/tour/tourAnchor'
import { attentionLine } from './attentionText'

type Values = Record<string, unknown>

/** The generated form's props. A custom module's task view takes exactly
 * these (E5 spec §7.1, `TaskViewProps` in `src/custom/index.ts`), so it
 * completes through the page's `onComplete` like the form does. */
export interface TaskFormProps {
  task: TaskRead
  onComplete: (exit: string, fields: Record<string, unknown>) => void
  onSaveDraft: (fields: Record<string, unknown>) => void
  busy: boolean
}

export function TaskForm({ task, onComplete, onSaveDraft, busy }: TaskFormProps) {
  const [values, setValues] = useState<Values>(() =>
    Object.fromEntries(task.form.map((f) => [f.id, f.value ?? (f.type === 'boolean' ? false : null)])),
  )
  // SA-1196 D2: the "Suggested by AI" chip follows the equality rule inside
  // FieldBlock (it shows while the value equals the served pre-fill), so the
  // form keeps no client-side `touched` set.
  const proposal = task.proposal ?? null
  const change = (id: string, v: unknown) => setValues((s) => ({ ...s, [id]: v }))
  // SA-449: an empty table is blank, like the core's `_is_blank`.
  const filled = (id: string) => {
    const v = values[id]
    return v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0)
  }
  const baseRequired = useMemo(() => task.form.filter((f) => !f.optional).map((f) => f.id), [task.form])
  // E5 (spec §5.5): a form cannot be worked blind. The server's work gate is
  // the rule (403 `restricted_fields`); this only stops offering what would
  // 403. A restricted NEED alone leaves the form workable (§5.4).
  const restricted = task.form.some((f) => f.redacted)

  if (task.kind === 'wait' && task.surface === 'record.feasibility') {
    // SA-1076: nothing is completed here; say where the step is worked and go there.
    return (
      <div className="space-y-3 text-sm">
        <p>{waitTaskLine(task.step_number, task.step_title)}</p>
        <Link className={buttonVariants({ size: 'sm' })} to={`/orders?order=${task.order_id}&panel=feasibility`}>{RECORD_RESULT}</Link>
      </div>
    )
  }

  if (task.kind === 'wait' && task.surface === 'record.quotes') {
    // E4 (spec D20 §7.3): both quote waits end on the record's quote versions
    // card — a sent version ends the offer, a recorded response the next one.
    return (
      <div className="space-y-2 text-sm">
        <p>This step ends when a quote is sent (emailed from the Inbox, or marked sent on the record), or when the customer’s response to it is recorded.</p>
        <Link className="underline text-primary" to={`/orders?order=${task.order_id}&panel=quotes`}>Open the quote versions</Link>
      </div>
    )
  }

  const labelOf = (id: string) => task.form.find((f) => f.id === id)?.label ?? task.needs.find((n) => n.id === id)?.label ?? id
  const actions: TaskAction[] = [
    // SA-1419: a form with no fields has nothing to save (step 13's timer task).
    ...(task.form.length > 0 ? [{ id: 'save', label: SAVE_DRAFT, variant: 'outline', kind: 'work', onClick: () => onSaveDraft(values),
      blocked: restricted ? RESTRICTED_TASK : null, attrs: tourAnchor('task.form.save') } satisfies TaskAction] : []),
    ...task.exits.map((e): TaskAction => {
      const isSuggested = proposal !== null && proposal.exit === e.name
      const unfilled = [...new Set([...baseRequired, ...e.requires])].filter((id) => !filled(id))
      return {
        id: e.name, label: e.label, kind: 'work', onClick: () => onComplete(e.name, values),
        blocked: restricted ? RESTRICTED_TASK : unfilled.length > 0 ? fillFirst(unfilled.map(labelOf)) : null,
        description: isSuggested ? SUGGESTED_BY_AI : undefined,
        icon: isSuggested ? <Sparkles aria-hidden="true" /> : undefined,
        className: isSuggested ? 'ring-2 ring-info ring-offset-2 ring-offset-background' : undefined,
      }
    }),
  ]

  const awaiting = task.kind === 'attention' && task.surface ? attentionLine(task.waiting_for_event, task.surface) : null

  return (
    <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
      {awaiting && (
        <div className="space-y-1 text-sm">
          <p>{awaiting.line}</p>
          <Link className="underline text-primary" to={`/orders?order=${task.order_id}&panel=${awaiting.panel}`}>{awaiting.link}</Link>
        </div>
      )}
      {task.guidance && <p className="whitespace-pre-wrap text-sm text-muted-foreground">{task.guidance}</p>}
      <div {...tourAnchor('task.form.needs')}>
        <NeedsList needs={task.needs} />
      </div>
      <div className="space-y-3" {...tourAnchor('task.form.fields')}>
        {task.form.map((f) => (
          <FieldBlock key={f.id} field={f} value={values[f.id]} onChange={(v) => change(f.id, v)} idPrefix="f" />
        ))}
      </div>
      {proposal?.rationale && <ProposalRationale rationale={proposal.rationale} />}
      <TaskActions actions={actions} busy={busy} attrs={tourAnchor('task.form.actions')} />
    </form>
  )
}
